import React, { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { Users, LogOut as LeaveIcon, Plus, X, Loader2, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Compact "My Classes" widget for the student dashboard.
 * Shows enrolled classes + "Join class" button → modal that takes
 * teacher_code + class_code.
 */
export default function StudentClassesCard() {
    const [classes, setClasses] = useState(null);
    const [showJoin, setShowJoin] = useState(false);

    const reload = () => {
        api.get('/api/student/classes').then((r) => setClasses(r.data || [])).catch(() => setClasses([]));
    };
    useEffect(reload, []);

    const leave = async (id) => {
        if (!window.confirm('Leave this class? Your past submissions remain.')) return;
        try {
            await api.delete(`/api/student/classes/${id}`);
            toast.success('Left class');
            reload();
        } catch { toast.error('Could not leave class'); }
    };

    if (classes === null) return null;

    return (
        <div className="bg-white border-2 border-zinc-950 rounded-2xl p-5 mb-6" data-testid="student-classes-card">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-black text-sm inline-flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#7C3AED]" /> My Classes</h3>
                <button onClick={() => setShowJoin(true)} className="text-xs font-bold text-[#7C3AED] inline-flex items-center gap-1" data-testid="open-join-class">
                    <Plus className="w-3.5 h-3.5" /> Join class
                </button>
            </div>
            {classes.length === 0 ? (
                <p className="text-xs text-zinc-500">Not in a class yet. Ask your teacher for a join code.</p>
            ) : (
                <ul className="space-y-1.5">
                    {classes.map((c) => (
                        <li key={c.enrollment_id} className="flex items-center justify-between text-sm group" data-testid={`student-class-${c.classroom_id}`}>
                            <div className="min-w-0">
                                <div className="font-bold truncate">{c.name}</div>
                                <div className="text-[10px] text-zinc-500 truncate">{c.subject} · {c.teacher_name}</div>
                            </div>
                            <button onClick={() => leave(c.enrollment_id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-zinc-400 hover:text-rose-600 p-1" title="Leave" data-testid={`leave-class-${c.classroom_id}`}>
                                <LeaveIcon className="w-3.5 h-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {showJoin && <JoinClassModal onClose={() => setShowJoin(false)} onJoined={() => { setShowJoin(false); reload(); }} />}
        </div>
    );
}

function JoinClassModal({ onClose, onJoined }) {
    const [teacherCode, setTeacherCode] = useState('');
    const [classCode, setClassCode] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!teacherCode.trim() || !classCode.trim()) return toast.error('Both codes required');
        setSaving(true);
        try {
            const r = await api.post('/api/student/join-class', {
                teacher_code: teacherCode.trim().toUpperCase(),
                class_code: classCode.trim().toUpperCase(),
            });
            if (r.data?.already_enrolled) toast.info("You're already in this class");
            else toast.success(`Joined ${r.data.classroom_name}`);
            onJoined();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not join class');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="join-class-modal">
            <div className="bg-white rounded-3xl border-2 border-zinc-950 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-black inline-flex items-center gap-2"><Users className="w-5 h-5 text-[#7C3AED]" /> Join a class</h3>
                    <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-lg"><X className="w-4 h-4" /></button>
                </div>
                <p className="text-xs text-zinc-500 mb-4">Ask your teacher for both codes (e.g. <strong className="text-[#7C3AED]">ECO-4821</strong>).</p>
                <form onSubmit={submit} className="space-y-3">
                    <Field label="Teacher code">
                        <input autoFocus value={teacherCode} onChange={(e) => setTeacherCode(e.target.value)} placeholder="e.g. ECO-4821" className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none focus:border-[#7C3AED] text-sm uppercase" data-testid="join-teacher-code" />
                    </Field>
                    <Field label="Class code">
                        <input value={classCode} onChange={(e) => setClassCode(e.target.value)} placeholder="e.g. ECO-N26L" className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none focus:border-[#7C3AED] text-sm uppercase" data-testid="join-class-code" />
                    </Field>
                    <button type="submit" disabled={saving} className="w-full bg-[#7C3AED] text-white font-bold text-sm py-3 rounded-xl hover:bg-[#6D28D9] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="join-class-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {saving ? 'Joining…' : 'Join class'}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className="text-xs font-bold text-zinc-600 mb-1 block">{label}</span>{children}</label>;
}
